/**
 * EL HISTÓRICO DE AJUSTES, COMPARTIDO (doc 19-08, bloque 09).
 *
 * La misma tabla vive en dos sitios según el plan: en «Mis macros» para el que se los
 * calcula él, y en Seguimiento → Evolución para el que se los lleva un entrenador («al
 * lado de su peso, sus medidas y sus fotos. Que es donde se ve que estás trabajando»).
 * Un solo componente para que nunca cuenten historias distintas.
 *
 * También sale de aquí la frase del último ajuste de la cabecera del doc:
 * «Último ajuste: −20 g de hidratos en entreno, −10 g de grasa en descanso».
 *
 * Desde el doc de Jesús del 2-09 (duda 37) se llama «Tus macros, ajuste a ajuste» y enseña
 * los cuatro últimos de entrada; el resto, detrás de «Ver los N ajustes».
 */
import React from 'react';
import TablaDeslizable from './ui/TablaDeslizable';
import { Link } from 'react-router-dom';
import { MACRO } from '../pages/ClientDashboard';
import { num1 } from '../lib/numeros';
import GraficaDeAjustes from './GraficaDeAjustes';
import { normalizarObjetivo } from '../lib/objetivos';

const CAMPOS = [
    { key: 'proteina', corto: 'P', nombre: 'proteína', color: MACRO.protein },
    { key: 'hidratos', corto: 'H', nombre: 'hidratos', color: MACRO.carbs },
    { key: 'grasa', corto: 'G', nombre: 'grasa', color: MACRO.fat },
];

const numero = (v) => (v === null || v === undefined ? '-' : v);

// EL OBJETIVO, CON SU PUNTO (rediseño de Enrique, 29-10): un punto de color y el nombre
// entero, en vez de la pastilla. Los colores son los de Jesús (7-09, regla 01·9): definición
// en naranja sólido, volumen en naranja perfilado, recomposición en ámbar y mantenimiento en
// gris; «máxima definición» va con la definición y «tonificación» en neutro.
const OBJETIVOS = {
    perder_grasa: { texto: 'Definición', punto: 'bg-brand' },
    maxima_definicion: { texto: 'Máx. definición', punto: 'bg-brand' },
    ganar_volumen: { texto: 'Volumen', punto: 'border-2 border-brand' },
    recomposicion: { texto: 'Recomposición', punto: 'bg-amber-500' },
    mantenimiento: { texto: 'Mantenimiento', punto: 'bg-muted-foreground' },
    tonificacion: { texto: 'Tonificación', punto: 'bg-muted-foreground' },
};

// DONDE NO HAY OBJETIVO NO SE PINTA NADA. Medido en producción el 7-09: el campo estaba en 52
// de 3.702 filas. Desde hoy se rellena al guardar y lo viejo se resuelve por el ciclo, pero
// para lo que no cae en ningún ciclo un hueco es más honesto que un «mantenimiento» por
// defecto, que es justo el valor que más veces mentiría.
const Objetivo = ({ clave }) => {
    const o = OBJETIVOS[normalizarObjetivo(clave) || ''];
    if (!o) return null;
    return (
        <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground/90">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.punto}`} aria-hidden="true" />
            {o.texto}
        </span>
    );
};

export const fechaCorta = (iso) => {
    if (!iso) return '-';
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-ES', {
        day: 'numeric', month: 'short', year: '2-digit',
    });
};

/**
 * «Último ajuste: −20 g de hidratos en entreno, −10 g de grasa en descanso».
 * Se compara el ajuste vigente con el anterior, bloque a bloque; lo que no se movió no se
 * nombra. Sin anterior (el primer ajuste) no hay frase.
 */
export const ultimoAjusteLegible = (entradas) => {
    if (!entradas || entradas.length < 2) return null;
    const [ahora, antes] = entradas;
    const BLOQUES = [['entreno', 'entreno'], ['peri', 'perientreno'], ['descanso', 'descanso']];
    const partes = [];
    for (const [key, nombre] of BLOQUES) {
        for (const c of CAMPOS) {
            const a = ahora?.[key]?.[c.key];
            const b = antes?.[key]?.[c.key];
            if (a == null || b == null) continue;
            const delta = a - b;
            if (!delta) continue;
            partes.push(`${delta > 0 ? '+' : '−'}${Math.abs(delta)} g de ${c.nombre} en ${nombre}`);
        }
    }
    return partes.length ? partes.join(', ') : null;
};

// LO QUE CAMBIÓ RESPECTO AL AJUSTE ANTERIOR, Y CALCULADO (Jesús, 7-09, regla 03·2: «es contra
// la fila anterior, no un dato guardado»). Desde el rediseño de Enrique (29-10) el número que
// cambió va en blanco y en negrita, con la diferencia debajo: lo que baja en azul y lo que
// sube en rojo. Lo que no cambió, apagado y sin nada debajo.
//
// OJO: la comparación es contra el array ENTERO, no contra las cuatro que se ven. Si no, la
// cuarta fila de la portada saldría sin marcar por no tener vecina a la vista.
const conSigno = (n) => `${n > 0 ? '+' : '−'}${num1(Math.abs(n))}`;

const Celdas = ({ macros, anterior, conGrasa = true }) => {
    const campos = conGrasa ? CAMPOS : CAMPOS.slice(0, 2);
    return campos.map((c, i) => {
        const ahora = macros?.[c.key];
        const antes = anterior?.[c.key];
        const delta = anterior != null && ahora != null && antes != null ? ahora - antes : 0;
        return (
            <td key={c.key}
                className={`px-3 py-3.5 text-right align-top tabular-nums ${i === 0 ? 'border-l border-border/60' : ''}`}>
                <span className={`block text-base leading-tight ${delta ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                    {numero(ahora)}
                </span>
                {delta !== 0 && (
                    <span className={`block text-[11px] font-semibold leading-tight mt-0.5 ${delta < 0 ? 'text-sky-400' : 'text-red-400'}`}>
                        {conSigno(delta)}
                    </span>
                )}
            </td>
        );
    });
};

// Cuántos ajustes se enseñan de entrada; el resto a un clic («Ver los 14 ajustes»).
// Eran doce, y con diez columnas cada uno la tabla se comía la pantalla. Jesús, doc del
// 2-09 (duda 37): «que salgan los cuatro últimos y el resto detrás de Ver los 14 ajustes,
// como ya está». Los cuatro últimos son los que cuentan la historia reciente; el resto
// sigue a un clic.
const AJUSTES_A_LA_VISTA = 4;

/**
 * `completo`: sin recorte ni botón, para la pantalla «Todo tu histórico» (la 03 del doc del
 * 7-09). En la portada se enseñan los cuatro últimos y el resto vive en esa pantalla.
 */
const HistorialDeMacros = ({ entradas, completo = false }) => {
    const filas = entradas || [];
    const visibles = completo ? filas : filas.slice(0, AJUSTES_A_LA_VISTA);
    // Con una sola entrada no hay escalera que leer.
    if (filas.length < 2) return null;
    return (
        <section className="space-y-2" data-testid="mis-macros-historico">
            {/* «Se llama Histórico de macros y peso, no "cómo han ido cambiando"» (Jesús,
                7-09, regla 01·7). Corrige al nombre que él mismo puso el 2-09, «Tus macros,
                ajuste a ajuste»: manda la decisión más reciente.

                En la pantalla completa no se pinta: allí el título ya es «Todo tu histórico»
                y repetirlo dejaba dos rótulos pegados diciendo lo mismo. */}
            {!completo && <p className="caption">Histórico de macros y peso</p>}
            {/* LA GRÁFICA, ENCIMA DE LA TABLA (regla 01·7 y pantalla 03). Va con el bloque
                entero porque el rótulo ya promete las dos cosas, «macros y peso», y porque las
                dos salen de las mismas filas: no pueden contarse distinto.

                Ella enseña doce ajustes aunque la tabla enseñe cuatro, y lo dice en su pie. No
                es un descuido: son dos preguntas distintas -- «¿por dónde voy?» y «¿qué números
                tengo?» -- y él las separó así. */}
            <GraficaDeAjustes entradas={filas} />
            {/* CUÁNTOS AJUSTES ENSEÑA LA TABLA, pegado a la tabla. Estaba arriba, al lado del
                título, y desde que hay gráfica quedaba diciendo «tus cuatro últimos ajustes»
                encima de doce barras. Cada rótulo, al lado de lo que cuenta. */}
            {/* Y SALE TAMBIÉN CON CUATRO JUSTOS (Jesús, 8-09: «abajo falta el texto»).
                La condición era `filas.length > visibles.length`, o sea «solo si hay más de
                cuatro», y con cuatro exactos se callaba: es el caso de su cuenta, que tiene
                cuatro. La condición estaba para que el rótulo no mintiera con tres ajustes,
                pero se pasó de frenada y tapó justo el caso en que sí es verdad.
                Con menos de cuatro se dice sin número, que es lo único que no miente. */}
            {!completo && visibles.length > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="mis-macros-cuantos">
                    {visibles.length >= AJUSTES_A_LA_VISTA
                        ? 'Tus cuatro últimos ajustes'
                        : 'Tus últimos ajustes'}
                </p>
            )}
            {/* LA TABLA, REDISEÑADA (Enrique, 29-10), la misma en «Tus cuatro últimos ajustes» y
                en «Todo tu histórico»: cabeceras en gris y en mayúsculas, los bloques separados
                por una línea, lo que cambió en blanco con su diferencia debajo, el objetivo con
                su punto y el peso con lo que se movió respecto al ajuste anterior.
                CON LA SEÑAL DE QUE HAY MÁS A LOS LADOS (punto 51). */}
            <TablaDeslizable className="rounded-2xl border border-border bg-card px-3 sm:px-5 py-2" fondo="from-card">
                <table className="w-full text-sm min-w-[720px]">
                    <thead>
                        <tr className="text-muted-foreground text-[11px] font-bold uppercase tracking-[0.12em]">
                            {/* La fecha se queda fija al desplazar (revisión del 2-09): en
                                móvil la tabla se va de lado y la fecha quedaba cortada. */}
                            <th rowSpan={2} className="pl-1 pr-3 pt-4 pb-3 text-left font-bold sticky left-0 z-10 bg-card">Fecha</th>
                            <th colSpan={3} className="px-3 pt-4 text-center font-bold border-l border-border/60">Entreno</th>
                            <th colSpan={2} className="px-3 pt-4 text-center font-bold border-l border-border/60">Peri</th>
                            <th colSpan={3} className="px-3 pt-4 text-center font-bold border-l border-border/60">Descanso</th>
                            {/* EL OBJETIVO ENTRE LOS MACROS Y EL PESO, y el peso al final (Jesús,
                                7-09, reglas 01·8 y 03·1). */}
                            <th rowSpan={2} className="px-4 pt-4 pb-3 text-left font-bold border-l border-border/60">Objetivo</th>
                            <th rowSpan={2} className="pl-3 pr-1 pt-4 pb-3 text-right font-bold">Peso</th>
                        </tr>
                        <tr className="text-muted-foreground/70 text-[10px] font-semibold uppercase border-b border-border/60">
                            <th className="px-3 pt-1 pb-3 text-right font-semibold border-l border-border/60">P</th><th className="px-3 pt-1 pb-3 text-right font-semibold">H</th><th className="px-3 pt-1 pb-3 text-right font-semibold">G</th>
                            <th className="px-3 pt-1 pb-3 text-right font-semibold border-l border-border/60">P</th><th className="px-3 pt-1 pb-3 text-right font-semibold">H</th>
                            <th className="px-3 pt-1 pb-3 text-right font-semibold border-l border-border/60">P</th><th className="px-3 pt-1 pb-3 text-right font-semibold">H</th><th className="px-3 pt-1 pb-3 text-right font-semibold">G</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibles.map((h, i) => {
                            // El ajuste anterior EN EL TIEMPO: las filas vienen de la más
                            // nueva a la más vieja, así que es la de debajo del array entero.
                            const previo = filas[i + 1];
                            // Lo que se movió el peso respecto al ajuste anterior: lo que baja
                            // en verde y lo que sube en rojo. Sin anterior o sin cambio, nada.
                            const deltaPeso = h.peso != null && previo?.peso != null
                                ? Math.round((h.peso - previo.peso) * 10) / 10 : 0;
                            return (
                                <tr key={h.id || i} className="border-b border-border/60 last:border-0">
                                    {/* SU FILA, MARCADA COMO «TÚ» (Jesús, 7-09, regla 07·6). */}
                                    <td className="pl-1 pr-3 py-3.5 whitespace-nowrap text-base text-foreground sticky left-0 z-10 bg-card align-middle">
                                        {fechaCorta(h.fecha)}
                                        {h.del_cliente && !h.del_cuestionario && (
                                            <span className="ml-2 text-[11px] font-bold uppercase tracking-wide text-brand"
                                                title="Este ajuste lo hiciste tú">tú</span>
                                        )}
                                    </td>
                                    <Celdas macros={h.entreno} anterior={previo?.entreno} />
                                    <Celdas macros={h.peri} anterior={previo?.peri} conGrasa={false} />
                                    <Celdas macros={h.descanso} anterior={previo?.descanso} />
                                    <td className="px-4 py-3.5 border-l border-border/60 align-middle">
                                        <Objetivo clave={h.objetivo} />
                                    </td>
                                    {/* Con coma y a la décima, la regla de la casa. */}
                                    <td className="pl-3 pr-1 py-3.5 text-right whitespace-nowrap tabular-nums align-top">
                                        {h.peso != null ? (
                                            <span className="block leading-tight">
                                                <span className="text-base font-bold text-foreground">{num1(h.peso)}</span>
                                                <span className="text-sm text-muted-foreground"> kg</span>
                                            </span>
                                        ) : <span className="block text-muted-foreground">-</span>}
                                        {deltaPeso !== 0 && (
                                            <span className={`block text-[11px] font-semibold leading-tight mt-0.5 ${deltaPeso < 0 ? 'text-ok' : 'text-red-400'}`}
                                                data-testid="historial-delta-peso">
                                                {conSigno(deltaPeso)} kg
                                            </span>
                                        )}
                                        {/* Y EL % GRASO DEBAJO DEL PESO, como en la ficha del
                                            entrenador (hoja del 22-10, fila 55). */}
                                        {h.grasa != null && (
                                            <span className="block text-[11px] text-muted-foreground" data-testid="historial-grasa">
                                                {num1(h.grasa)} %
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </TablaDeslizable>
            {/* «VER HISTÓRICO COMPLETO», Y LLEVA A OTRA PANTALLA (regla 01·11). Desplegaba
                aquí mismo las 264 filas del que más tiene, que es lo que él llama «un archivo
                en la portada». En la pantalla completa este botón no se pinta. */}
            {/* Y EL ENLACE SALE SIEMPRE QUE HAYA ALGO QUE VER. Colgaba de la misma
                condición que el rótulo, así que al que tiene cuatro ajustes o menos se le
                escondía la única puerta a «Todo tu histórico»: esa pantalla existe y no
                había forma de llegar a ella. Con la tabla enseñando lo mismo que hay, el
                enlace no promete de más: lleva a la pantalla donde además se compara. */}
            {!completo && filas.length > 0 && (
                <Link to="/dashboard/macro-calculator/historico"
                    className="inline-block text-xs font-semibold text-brand hover:underline"
                    data-testid="mis-macros-ver-todo">
                    {/* «Ver histórico de ajustes» (05-11, prueba e2e 7.9); era «Ver histórico completo». */}
                    Ver histórico de ajustes ›
                </Link>
            )}
        </section>
    );
};

export default HistorialDeMacros;
